/* eslint-disable max-lines */
import {
  ApiState,
  useApiStateRecord,
  ApiStateRecord,
  callApiAndUpdateState
} from '@exxonmobil/uit-uda-ui-components-lib';

type BuiltInApiParamsType = {
  selectedPads: string[];
};

type BaseApiParamsType = {
  [param: string]: any;
};

export type ApiServiceFunctionParams<ApiParamsType extends BaseApiParamsType> =
  ApiParamsType & BuiltInApiParamsType;

export type OnResponseEventHandler<x> = (state: ApiState<x>) => Promise<void>;

export type ApiServiceFunction<ApiParamsType extends BaseApiParamsType> = (
  params: ApiServiceFunctionParams<ApiParamsType>
) => Promise<Response | any>;

export type ApiFunction<
  ApiParamsType extends BaseApiParamsType,
  StateDataType
> = (
  params?: ApiParamsType,
  stateRecord?: ApiStateRecord<StateDataType>
) => Promise<ApiState>;

export type UseApiHookReturn<
  ApiParamsType extends BaseApiParamsType,
  StateDataType
> = [ApiStateRecord<StateDataType>, ApiFunction<ApiParamsType, StateDataType>];

export type CreateApiHookDataFilter<
  ApiParamsType extends BaseApiParamsType,
  StateDataType
> = (
  state: ApiState<StateDataType>,
  params: ApiParamsType & BuiltInApiParamsType
) => StateDataType;

export type CreateApiHookParams<
  ApiParamsType extends BaseApiParamsType,
  StateDataType
> = {
  apiServiceFunction: ApiServiceFunction<ApiParamsType>;
  dataFilter?: CreateApiHookDataFilter<ApiParamsType, StateDataType>;
  initStateDataValue: StateDataType;
};

export const createApiHook = <
  ApiParamsType extends BaseApiParamsType,
  StateDataType
>({
  apiServiceFunction,
  dataFilter,
  initStateDataValue
}: CreateApiHookParams<ApiParamsType, StateDataType>) => {
  const useApiHook = (): UseApiHookReturn<ApiParamsType, StateDataType> => {
    const apiStateRecord = useApiStateRecord(initStateDataValue);

    const api = async (
      params?: ApiParamsType,
      stateRecord?: ApiStateRecord<StateDataType>
    ): Promise<ApiState> => {
      const newParams = {
        ...(params && params)
      } as ApiParamsType & BuiltInApiParamsType;

      const stateRecordToUse = stateRecord || apiStateRecord;
      await callApiAndUpdateState({
        stateRecord: stateRecordToUse,
        apiServiceFunction: async () => apiServiceFunction(newParams),
        onStateChange: (state: ApiState): ApiState => {
          const { loading, responseStatus, error } = state;
          const isDataAvailable = !loading && responseStatus > 0 && !error;

          if (!dataFilter || !isDataAvailable) {
            return state;
          }
          const filteredDataState = { ...state };
          filteredDataState.data = dataFilter(filteredDataState, newParams);
          return filteredDataState;
        }
      });

      return stateRecordToUse.state;
    };

    return [apiStateRecord, api];
  };
  return useApiHook;
};
